import {
  cn,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@aircall/ds";
import {
  type ComponentPropsWithoutRef,
  type ForwardedRef,
  forwardRef,
  type ReactNode,
  type Ref,
} from "react";

import {
  type EditableValidationStatus,
  editableFieldVariants,
} from "./Editable.variants";

export type EditableSelectOption<TValue> = {
  label: ReactNode;
  value: TValue;
};

export interface EditableSelectProps<TValue>
  extends Omit<
    ComponentPropsWithoutRef<"button">,
    "value" | "defaultValue" | "onChange"
  > {
  /**
   * Options shown in the popup. Mirrors the DS / Base UI `Select` primitive's
   * own items-driven API: the array is passed to the Select root so the trigger
   * can resolve the selected option's *label* (not its raw value).
   */
  options: ReadonlyArray<EditableSelectOption<TValue>>;
  /** Controlled value. */
  value?: TValue;
  /** Uncontrolled initial value. */
  defaultValue?: TValue;
  /** Placeholder shown when no value is selected. */
  placeholder?: ReactNode;
  /** Fired with the selected value when an option is picked. */
  onChange?: (value: TValue) => void;
  /** Visual validation state of the inline trigger. */
  validationStatus?: EditableValidationStatus;
  /** Whether the trigger is disabled. */
  disabled?: boolean;
  /** Extra classes merged onto the trigger (e.g. typography). */
  className?: string;
  "data-test"?: string;
}

/**
 * Inline-editable select: a ghost trigger that reveals the chosen option's
 * label (falling back to a placeholder), opening a DS Select popup on click.
 *
 * Composes the DS `Select` compound. This is a structureless leaf — its parts
 * (trigger + popup) are always assembled the same way and the consumer cannot
 * meaningfully recompose them — so it follows the leaf-uses-variants rule
 * rather than re-exporting Select sub-parts. The `options` prop is faithful
 * composition, not a data-prop monolith: the DS / Base UI `Select` root is
 * itself items-driven (it resolves the selected *label* from `items`), so the
 * array must reach the root for the trigger to show "Yes" instead of "true".
 *
 * Generic over the option value type so a consumer can use string, boolean, or
 * any serialisable value. `forwardRef` exposes the underlying trigger button.
 */
function EditableSelectInner<TValue>(
  componentProps: EditableSelect.Props<TValue>,
  forwardRef: ForwardedRef<HTMLButtonElement>
) {
  const {
    options,
    value,
    defaultValue,
    placeholder,
    onChange,
    validationStatus = "success",
    disabled,
    className,
    "data-test": dataTest = "editable-select",
    ...triggerProps
  } = componentProps;

  return (
    <Select<TValue>
      items={options as { label: ReactNode; value: TValue }[]}
      value={value}
      defaultValue={defaultValue}
      disabled={disabled}
      onValueChange={(nextValue) => {
        if (nextValue !== null) {
          onChange?.(nextValue);
        }
      }}
    >
      <SelectTrigger
        {...triggerProps}
        ref={forwardRef}
        data-slot="editable-select-trigger"
        data-test={`${dataTest}-trigger`}
        data-validation-status={validationStatus}
        className={cn(
          editableFieldVariants({ validationStatus }),
          // Reserve a transparent border so showing it on open causes no shift.
          // The open-state border/background colours live in style.css (they must
          // beat the field's hover background). The focus ring is dropped here so
          // it doesn't flicker as Base UI moves focus into the popup on open.
          "h-auto border border-transparent shadow-none focus:ring-0",
          className
        )}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent
        data-slot="editable-select-content"
        className="w-(--anchor-width)"
      >
        {options.map((option: EditableSelectOption<TValue>) => (
          <SelectItem
            key={String(option.value)}
            value={option.value}
            data-slot="editable-select-item"
            data-test={`${dataTest}-option-${String(option.value)}`}
          >
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * `forwardRef` drops the generic, so we cast back to a generic-preserving call
 * signature. This keeps `<EditableSelect<boolean> />` fully typed at call sites.
 */
export const EditableSelect = forwardRef(EditableSelectInner) as (<TValue>(
  props: EditableSelect.Props<TValue> & { ref?: Ref<HTMLButtonElement> }
) => ReturnType<typeof EditableSelectInner>) & { displayName?: string };

EditableSelect.displayName = "EditableSelect";

export namespace EditableSelect {
  export type Props<TValue> = EditableSelectProps<TValue>;
}
